import React, { Suspense, useEffect, useState } from "react";
import {
    graphql,
    PreloadedQuery,
    useMutation,
    usePreloadedQuery,
    useQueryLoader,
} from "react-relay";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";

import { Caption, Subtitle2 } from "common/DS/Text";

import { ContactInfoNudgeQuery as ContactInfoNudgeQueryType } from "__generated__/ContactInfoNudgeQuery.graphql";
import { ContactInfoNudgeDismissMutation as ContactInfoNudgeDismissMutationType } from "__generated__/ContactInfoNudgeDismissMutation.graphql";

const ContactInfoNudgeQuery = graphql`
    query ContactInfoNudgeQuery {
        me {
            id
            fullName
            companyName
            email
            phone
            contactInfoNudge
        }
    }
`;

const ContactInfoNudgeDismissMutation = graphql`
    mutation ContactInfoNudgeDismissMutation {
        dismissContactInfoNudge {
            me {
                id
                contactInfoNudge
            }
        }
    }
`;

type Props = {
    queryRef: PreloadedQuery<ContactInfoNudgeQueryType>;
};

const Field = ({ label, value }: { label: string; value: string }) => (
    <div>
        <Caption className="color-navy-medium">{label}</Caption>
        <Subtitle2 className="color-navy-high">{value || "—"}</Subtitle2>
    </div>
);

const ContactInfoNudgeContent = ({ queryRef }: Props) => {
    const { me } = usePreloadedQuery(ContactInfoNudgeQuery, queryRef);
    const [dismiss] = useMutation<ContactInfoNudgeDismissMutationType>(
        ContactInfoNudgeDismissMutation,
    );
    const [open, setOpen] = useState(me.contactInfoNudge);

    if (!open) {
        return null;
    }

    const onLooksGood = () => {
        setOpen(false);
        dismiss({
            variables: {},
            optimisticResponse: {
                dismissContactInfoNudge: {
                    me: { id: me.id, contactInfoNudge: false },
                },
            },
        });
    };

    const onCloseOnly = () => {
        setOpen(false);
    };

    const onUpdate = () => {
        window.location.href = "/hub/contact-info";
    };

    return (
        <Dialog open onClose={onCloseOnly} fullWidth maxWidth="xs">
            <DialogTitle>Your Contact Info</DialogTitle>
            <DialogContent>
                <Subtitle2 className="color-navy-high mb-md">
                    Ensure we have the right contact info for you so you receive announcements and
                    opportunities to give {me.companyName || "your team"} feedback.
                </Subtitle2>
                <Stack spacing={1.5}>
                    <Field label="Name" value={me.fullName} />
                    <Field label="Email" value={me.email} />
                    <Field label="Phone" value={me.phone} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onLooksGood}>
                    Looks Good
                </Button>
                <Button variant="contained" onClick={onUpdate}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ContactInfoNudgeWrapper = () => {
    const [queryRef, loadQuery, disposeQuery] =
        useQueryLoader<ContactInfoNudgeQueryType>(ContactInfoNudgeQuery);

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, [loadQuery, disposeQuery]);

    if (!queryRef) return null;

    return <ContactInfoNudgeContent queryRef={queryRef} />;
};

const ContactInfoNudge = () => (
    <Suspense fallback={null}>
        <ContactInfoNudgeWrapper />
    </Suspense>
);

export default ContactInfoNudge;
